/**
 * Explore segments as hub slugs.
 *
 * A segment (`restaurants`, `locations`, `nightlife`, …) sits in the same URL
 * position as a bookable category — `/{lang}/category/{slug}/{city}` — but is
 * stored on `activities.segment`, not in the categories table. Filtering a
 * segment hub by `category` therefore matches nothing, which is what left
 * `/en/category/restaurants/marrakech` rendering an empty grid and marking
 * itself `noindex,follow` while 93 Marrakech restaurants sat one filter away.
 *
 * Lives in `lib/` rather than in the query or SEO layer because both need it
 * and neither should import the other.
 */
import { EXPLORE_TEMPLATE_BY_SEGMENT } from "@majourneys/shared";

/**
 * Segment slugs that are ALSO bookable category slugs.
 *
 * `/category/spas/{city}` and `/category/stays/{city}` are one page to a
 * visitor but two disjoint sets in the data: the 3 bookable spas in Marrakech
 * carry a category, the 8 explore ones carry a segment. Neither `category=` nor
 * `segment=` can return all 11, so these hubs are sent to the API's `hub=`
 * union instead of either single-sided filter.
 */
export const SEGMENT_SLUGS_THAT_ARE_ALSO_CATEGORIES = new Set(["spas", "stays", "restaurants"]);

/**
 * Does this hub slug need the `hub=` union — i.e. does it name a bookable
 * category and an explore segment at the same time?
 */
export function isUnionHubSlug(slug: string): boolean {
  return (
    slug in EXPLORE_TEMPLATE_BY_SEGMENT &&
    SEGMENT_SLUGS_THAT_ARE_ALSO_CATEGORIES.has(slug)
  );
}

/**
 * Hubs whose SERP copy stays editorial even though the union returns a few
 * bookable rows.
 *
 * Restaurants is the case. It is a union hub in the query — we sell four of
 * them and those have to lead the grid — but 327 of its 331 rows are places we
 * write about and cannot reserve. The bookable description template ends
 * "Reserve free, pay on arrival", which on a restaurants hub is a promise we
 * cannot keep on all but four pages. Spas and stays sell enough of their own
 * inventory to earn that sentence; restaurants does not.
 *
 * Kept apart from `isUnionHubSlug` on purpose: one answers "which filter does
 * the API need", the other "what can this page honestly claim in a snippet".
 * They were the same question until restaurants made them different.
 */
export function usesEditorialHubCopy(slug: string): boolean {
  return (
    slug in EXPLORE_TEMPLATE_BY_SEGMENT &&
    !(slug === "spas" || slug === "stays")
  );
}

/**
 * Does this hub slug name an explore segment rather than a bookable category?
 *
 * Driven off the shared `EXPLORE_TEMPLATE_BY_SEGMENT` map so a segment added
 * there gets a working hub on the same day, instead of silently falling back
 * to the empty-grid path.
 */
export function isExploreSegmentSlug(slug: string): boolean {
  return (
    slug in EXPLORE_TEMPLATE_BY_SEGMENT &&
    !SEGMENT_SLUGS_THAT_ARE_ALSO_CATEGORIES.has(slug)
  );
}

/**
 * Segment slug to i18n key for its display name.
 *
 * Reuses the `discover.bucket.*` labels, which already ship in all seven
 * locales, rather than adding a parallel set that would drift. It also fixes
 * the slug-titleised names these hubs would otherwise show: the segment is
 * called `locations` internally, but the page has to say "Attractions", which
 * is both the correct English and the term people search for.
 */
export const SEGMENT_LABEL_KEY: Record<string, string> = {
  locations: "discover.bucket.locations",
  restaurants: "discover.bucket.restaurants",
  spas: "discover.bucket.spas",
  "beach-clubs": "discover.bucket.beach_clubs",
  nightlife: "discover.bucket.nightlife",
  golf: "discover.bucket.golf",
  stays: "discover.bucket.stays",
  guides: "discover.bucket.guides",
};
